import React, { useState, useMemo } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer } from "../../utils/helpers";
import { updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import { modalConfirm, modalQuestionResolution, modalHint, modalHintShow } from "../../utils/logger";
import { buildQuizFeedbackBlockHtml } from "../../utils/quizFeedbackHtml";
import { IconHandClick } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import estasseguro from "../../assets/bg/estasseguro-avatar-blue.png";
import respuestacorrecta from "../../assets/bg/correcto-avatar.png";
import respuestaincorrecta from "../../assets/bg/incorrecto-avatar.png";
import btnPistaBlue from "../../assets/btnPistaBlue.png";
import imageHint from "../../assets/hints/imageHintBlue.png";
import imageHintUsed from "../../assets/btnPistaBlue.png";
import unIntento from "../../assets/un-intento-blue.png";
import dosIntentos from "../../assets/dos-intentos-blue.png";

const ACCENT = "#69D0FF";

const slotPlaceholderStyle = {
	borderBottom: "2px dashed #bbb",
	color: "#999",
	minWidth: "56px",
	display: "inline-block",
	margin: "0 2px",
};

function Scenario4Quiz3({ intl }) {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const location = useLocation();
	const group = useSelector((state) => state.group);

	const activeScenario = getScenarioNumByPath(location.pathname);

	const QUIZ_ID = QUIZZES.QUIZ9;
	const SCENARIO_ID = SCENARIOS.SCENARIO_4;

	const [selectedOption, setSelectedOption] = useState(null);
	const [isCompleted, setIsCompleted] = useState(false);
	const [hintUsed, setHintUsed] = useState(false);
	const [hintPenalty, setHintPenalty] = useState(0);
	const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [attempt, setAttempt] = useState(1);
	const [blockedOptions, setBlockedOptions] = useState(new Set());
	const [pendingScore, setPendingScore] = useState(0);

	const NUMBERATTEMPTS = 2;
	const CORRECT_ANSWER_POINTS = 10;
	const WRONG_ANSWER_POINTS = -5;
	const CORRECT_ANSWER_POINTS_2 = 3;
	const WRONG_ANSWER_POINTS_2 = 0;

	const retroalimentacion =
		"La mayoría de los pacientes en TITAN (78,3%) presentaban enfermedad sincrónica (M1 de inicio), al igual que Santiago. El 16,2% restante correspondía a enfermedad de origen metacrónico (M0 inicial).";
	const retroalimentacionBlockHtml = buildQuizFeedbackBlockHtml(retroalimentacion, "blue");

	/** parts: [ % antes de (M1), término (M1), % antes de (M0), término (M0) ] — mismo orden que en la frase */
	const options = [
		{
			id: 1,
			correct: false,
			line: "16,2% / sincrónica / 78,3% / metacrónica",
			parts: ["16,2%", "SINCRÓNICA", "78,3%", "metacrónica"],
		},
		{
			id: 2,
			correct: false,
			line: "78,3% / metacrónica / 16,2% / sincrónica",
			parts: ["78,3%", "metacrónica", "16,2%", "SINCRÓNICA"],
		},
		{
			id: 3,
			correct: true,
			line: "78,3% / sincrónica / 16,2% / metacrónica",
			parts: ["78,3%", "SINCRÓNICA", "16,2%", "metacrónica"],
		},
		{
			id: 4,
			correct: false,
			line: "62% / sincrónica / 38% / metacrónica",
			parts: ["62%", "SINCRÓNICA", "38%", "metacrónica"],
		},
	];

	const shuffledOptions = useMemo(() => [...options].sort(() => Math.random() - 0.5), []);

	async function submitAnswer(finalScore, correct) {
		const totalTime = getTimer();
		dispatch(updateTime(totalTime, 1));

		try {
			const res = await postAnswer({
				scenario: SCENARIO_ID,
				quiz: QUIZ_ID,
				group: group?._id,
				score: finalScore,
				time: totalTime,
				lastVisited: ROUTES.SCENARIO4_QUIZ4,
				correct,
			});

			if (res.status === 201) {
				const serverGroup = res.data?.group;
				if (serverGroup) {
					dispatch({ type: "GROUP_SET", payload: serverGroup });
				}
				setIsCompleted(true);
				navigate(ROUTES.SCENARIO4_QUIZ4);
			}
		} catch (error) {
			const status = error?.response?.status;
			const code = error?.response?.data?.code;

			if (status === 409 && code === "E_DUPLICATE_ANSWER") {
				await modalQuestionResolution({
					correct: false,
					question: "Respuesta ya registrada",
					textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, pueden continuar con el resto del juego.</div>`,
					customConfirmButtonHtml: "Continuar",
				});
				navigate(ROUTES.SCENARIO4_QUIZ4);
				return;
			}

			await modalQuestionResolution({
				correct: false,
				question: "No se pudo guardar",
				textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
				customConfirmButtonHtml: "Aceptar",
			});
			setIsSubmitting(false);
		}
	}

	const handleHint = async () => {
		const hint =
			"Asocia la gran mayoría de la muestra (casi el 80%) con el término médico para el debut al mismo tiempo (sincrónico), y el porcentaje restante (~16%) con la enfermedad que aparece tras un diagnóstico previo (metacrónico).";
		if (hintUsed) {
			await modalHintShow({ hint, imageHintUsed, color: "blue", big: false });
			return;
		}
		const { used } = await modalHint({ image: imageHint, imageHintUsed, hint, color: "blue", big: false });
		if (used && !hintUsed) {
			setHintUsed(true);
			setHintPenalty(3);
		}
	};

	function pickOption(optionId) {
		if (blockedOptions.has(optionId)) return;
		setSelectedOption((prev) => (prev === optionId ? null : optionId));
	}

	function checkAnswer() {
		if (selectedOption == null) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<img src=${estasseguro} alt="Selección requerida" class='estasseguro' /><div class="text-center txtTitulos mt-3 blue">Seleccionen una combinación para completar la frase</div>`,
			});
			return;
		}

		const option = options.find((o) => o.id === selectedOption);
		if (!option) return;

		if (blockedOptions.has(option.id)) {
			modalQuestionResolution({
				correct: false,
				textHtml: `<div class="txt24 txt-red bold">Esa opción ya la intentaron y era incorrecta. Elijan otra.</div>`,
				customConfirmButtonHtml: "Aceptar",
			});
			return;
		}

		modalConfirm({ estasseguro, color: "blue" }).then((res) => {
			if (!res.isConfirmed) return;
			setIsSubmitting(true);

			const hintThisAttempt = hintUsed && !hintAppliedToScore ? hintPenalty : 0;

			if (option.correct) {
				const base = attempt === 1 ? CORRECT_ANSWER_POINTS : CORRECT_ANSWER_POINTS_2;
				const attemptDelta = base - hintThisAttempt;
				const newScore = pendingScore + attemptDelta;
				if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

				modalQuestionResolution({
					correct: true,
					question: "Metástasis en TITAN",
					textHtml: `<img src=${respuestacorrecta} alt="Respuesta correcta" style='max-width:320px' />
                      <div class="txt28 bold pt-4">Ganaron ${attemptDelta} puntos en este intento${hintThisAttempt ? " (-3 pista)" : ""}</div>
                      ${attempt === 2 ? `<div class="txt20 mt-2">Correcto en el segundo intento</div>` : ""}
                      <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>
                      ${retroalimentacionBlockHtml}`,
					customConfirmButtonHtml: "Continuar",
					color: "blue",
				}).then(() => {
					submitAnswer(newScore, true);
				});
				return;
			}

			if (attempt < NUMBERATTEMPTS) {
				const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt;
				const newScore = pendingScore + attemptDelta;
				if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

				modalQuestionResolution({
					correct: false,
					question: option.line,
					textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:320px' />
                      <div class="txt28 bold pt-3">Combinación incorrecta. ${attemptDelta} puntos${hintThisAttempt ? " (incluye -3 por pista)" : ""}</div>
                      <div class="txt22 mt-2">Les queda <b>1</b> intento. Elijan otra opción.</div>
                      <div class="txt20 mt-2">Acumulado hasta ahora: <b>${newScore}</b></div>`,
					customConfirmButtonHtml: "Intentar de nuevo",
					color: "blue",
				}).then(() => {
					setPendingScore(newScore);
					setBlockedOptions((prev) => new Set(prev).add(option.id));
					setAttempt(attempt + 1);
					setSelectedOption(null);
					setIsSubmitting(false);
				});
				return;
			}

			const attemptDelta = WRONG_ANSWER_POINTS_2 - hintThisAttempt;
			const newScore = pendingScore + attemptDelta;
			if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

			modalQuestionResolution({
				correct: false,
				question: option.line,
				textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:320px' />
                    <div class="txt28 bold pt-4">Han agotado los intentos</div>
                    ${hintThisAttempt ? `<div class="txt20 mt-2">Se aplicó -3 por pista en este intento</div>` : ""}
                    <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>
                    ${retroalimentacionBlockHtml}`,
				customConfirmButtonHtml: "Continuar",
				color: "blue",
			}).then(() => {
				submitAnswer(newScore, false);
			});
		});
	}

	const remainingAttempts = NUMBERATTEMPTS - attempt + 1;
	const active = selectedOption != null ? options.find((o) => o.id === selectedOption) : null;

	function renderSlot(index) {
		if (active?.parts?.[index] != null) {
			return (
				<strong className="txt18" style={{ color: ACCENT }}>
					{active.parts[index]}
				</strong>
			);
		}
		return (
			<span className="txt16" style={slotPlaceholderStyle}>
				_______
			</span>
		);
	}

	return (
		<div className="h-100">
			<div className="container-fluid h-100 px-5 d-flex flex-column justify-content-start pt-5 bgCircleBlue">
				<h3 className="txt24 txt-white text-center" style={{ backgroundColor: "#69D0FF", borderRadius: "10px", padding: "20px"}}>
				Para validar la solidez de la evidencia que sustenta el tratamiento de Santiago, completen el siguiente reporte sobre la población del estudio clínico de referencia:
				</h3>

				<h3 className="mb-4 txt20 text-left my-2 gray">
					<IconHandClick size={22} color="#757575" stroke={1} /> Pulsen la combinación correcta para completar la frase
				</h3>

				<p className="txt22 mb-3" style={{ lineHeight: 1.55 }}>
					El estudio TITAN mostró una población predominantemente de debut, donde el {renderSlot(0)} de los pacientes
					presentaba enfermedad {renderSlot(1)} (M1), mientras que el {renderSlot(2)} correspondía a
					enfermedad {renderSlot(3)}.
				</p>

				<div className="row g-3 mb-2">
					{shuffledOptions.map((option) => {
						const isSelected = selectedOption === option.id;
						const isBlocked = blockedOptions.has(option.id);
						return (
							<div key={option.id} className="col-md-6">
								<button
									type="button"
									className="w-100 h-100 txt16 bold rounded shadow-sm border-0 py-3 px-2"
									style={{
										backgroundColor: isSelected ? `${ACCENT}44` : "#fff",
										color: ACCENT,
										boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
										outline: isSelected ? `2px solid ${ACCENT}` : "none",
										outlineOffset: 2,
										opacity: isBlocked ? 0.55 : 1,
										cursor: isBlocked ? "not-allowed" : "pointer",
									}}
									disabled={isBlocked}
									onClick={() => pickOption(option.id)}
									title={
										isBlocked
											? "Opción bloqueada (ya fue incorrecta)"
											: isSelected
												? "Clic de nuevo para vaciar la frase"
												: "Completar la frase con esta combinación"
									}
								>
									<div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2">
										<span className="flex-grow-1 text-center" style={{ minWidth: 0 }}>
											{option.line}
										</span>
										{isBlocked && <span className="badge bg-secondary ms-auto flex-shrink-0">Bloqueada</span>}
									</div>
								</button>
							</div>
						);
					})}
				</div>

				<div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
					<img src={remainingAttempts === 2 ? dosIntentos : unIntento} className="imgIntentos" alt="" />
				</div>

				<div className="d-flex justify-content-between mt-3 mb-2">
					<div className="position-relative d-inline-block">
						<img
							src={btnPistaBlue}
							className={`btnPista mt-2 ${hintUsed ? "used" : ""}`}
							onClick={handleHint}
							onKeyDown={(e) => {
								if (e.key === "Enter") void handleHint();
							}}
							alt={hintUsed ? "Pista usada" : "Usar pista"}
							title={hintUsed ? "Pista usada (-3)" : "Usar pista (-3 puntos)"}
							role="button"
							tabIndex={0}
						/>
						{hintUsed && <span className="badge-penalty">-3</span>}
					</div>
					<CheckButton
						text="Comprobar respuesta"
						onClick={checkAnswer}
						disabled={isCompleted || isSubmitting}
						color="blue"
						loading={isSubmitting}
					/>
				</div>
			</div>

			<ScenarioBar activeScenario={activeScenario} />
		</div>
	);
}

export default injectIntl(Scenario4Quiz3);
